import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { PaginatedCasingSearchResponse } from '../../features/search/model/casing-search-response.model';
import { CasingSearchResult } from '../../features/search/model/casing-search-result.model';

export class UpdateCasingSearchResultsAction {
  static readonly type = '[CasingSearch] Update Search Results';
  constructor(public searchResults: PaginatedCasingSearchResponse) {}
}

export class ClearCasingSearchResultsAction {
  static readonly type = '[CasingSearch] Clear Search Results';
}

export class UpdateSelectedCasingsAction {
  static readonly type = '[CasingSearch] Update Selected Casings';
  constructor(public selectedCasings: CasingSearchResult[]) {}
}

export class UpdateSearchCriteriaAction {
  static readonly type = '[CasingSearch] Update Search Criteria';
  constructor(public searchCriteria: any) {}
}

export class ResetCasingSearchStateAction {
  static readonly type = '[CasingSearch] Reset State';
}

export class UpdateIsRestoreStateAction {
  static readonly type = '[CasingSearch] Update IsRestoreState';
  constructor(public isRestoreState: boolean) {}
}

export interface CasingSearchStateModel {
  loading: boolean;
  searchResults: PaginatedCasingSearchResponse | null;
  selectedCasings: CasingSearchResult[];
  searchCriteria: any | null;
  hasSearched: boolean;
  isRestoreState: boolean;
}

const defaultState: CasingSearchStateModel = {
  loading: false,
  searchResults: null,
  selectedCasings: [],
  searchCriteria: null,
  hasSearched: false,
  isRestoreState: false
};

const getDefaultState = () => ({ ...defaultState });

@State<CasingSearchStateModel>({
  name: 'casingSearch',
  defaults: getDefaultState()
})
@Injectable()
export class CasingSearchState {
  constructor() {}

  @Action(UpdateCasingSearchResultsAction)
  updateSearchResults({ patchState }: StateContext<CasingSearchStateModel>, { searchResults }: UpdateCasingSearchResultsAction) {
    patchState({
      searchResults,
      hasSearched: true,
      loading: false
    });
  }

  @Action(ClearCasingSearchResultsAction)
  clearSearchResults({ patchState }: StateContext<CasingSearchStateModel>) {
    patchState({
      searchResults: null,
      selectedCasings: [],
      searchCriteria: null,
      hasSearched: false,
      loading: false
    });
  }

  @Action(UpdateSelectedCasingsAction)
  updateSelectedCasings({ patchState }: StateContext<CasingSearchStateModel>, { selectedCasings }: UpdateSelectedCasingsAction) {
    patchState({
      selectedCasings
    });
  }

  @Action(UpdateSearchCriteriaAction)
  updateSearchCriteria({ patchState }: StateContext<CasingSearchStateModel>, { searchCriteria }: UpdateSearchCriteriaAction) {
    patchState({
      searchCriteria
    });
  }

  @Action(ResetCasingSearchStateAction)
  resetState({ patchState }: StateContext<CasingSearchStateModel>) {
    patchState(getDefaultState());
  }

  @Action(UpdateIsRestoreStateAction)
  setIsRestoreState({ patchState }: StateContext<CasingSearchStateModel>, { isRestoreState }: UpdateIsRestoreStateAction) {
    patchState({ isRestoreState });
  }

  @Selector()
  static state(state: CasingSearchStateModel) {
    return state;
  }

  @Selector()
  static loading(state: CasingSearchStateModel) {
    return state.loading;
  }

  @Selector()
  static searchResults(state: CasingSearchStateModel) {
    return state.searchResults;
  }

  @Selector()
  static selectedCasings(state: CasingSearchStateModel) {
    return state.selectedCasings;
  }

  @Selector()
  static hasSearched(state: CasingSearchStateModel) {
    return state.hasSearched;
  }

  @Selector()
  static hasResults(state: CasingSearchStateModel) {
    return state.searchResults && state.searchResults.casingSearchResults && state.searchResults.casingSearchResults.length > 0;
  }

  @Selector()
  static searchCriteria(state: CasingSearchStateModel) {
    return state.searchCriteria;
  }

  @Selector()
  static isRestoreState(state: CasingSearchStateModel) {
    return state.isRestoreState;
  }
}
